<template>
    <seismic-layout>
        <main class="attachment-page">
            <section class="header">
                <h2>4. Define Structure Attachment and Pipe Attachment</h2>
            </section>
            <section class="attachment">
                <fieldset class="flex">
                    <div class="header">
                        <h3>Define the Structure</h3>
                        <div class="help">
                            <i class="fa fa-question inline-popup" data-mfp-src="#define-structure-help"></i>
                        </div>
                    </div>

                    <div v-if="options.structure && options.structure.length < 5" class="messaging info">
                        <p>Because of your load, not all structure options are available.</p>
                    </div>

                    <div>
                        <label for="structure">
                            Structure
                        </label>
                        <div class="select">
                            <select id="structure" placeholder="Select Your Structure" v-model="values.structure" @change="updateOptions('structure')">
                                <option v-for="option in options.structure" :value="option" v-text="option"></option>
                            </select>
                        </div>
                        <div class="text user-defined" v-if="isUserDefined(values.structure)">
                            <input type="text" id="structure-other" placeholder="Enter Your Structure" v-model="values.udStructure">
                        </div>
                    </div>
                    <div v-if="options.substrate && options.substrate.length">
                        <label for="substrate">
                            Substrate
                        </label>
                        <div v-if="options.substrate && options.substrate.length == 1" v-text="options.substrate[0]"></div>
                        <div v-else class="select">
                            <select id="substrate" placeholder="Select Your Substrate" v-model="values.substrate" @change="updateOptions('substrate')">
                                <option v-for="option in options.substrate" :value="option" v-text="option"></option>
                            </select>
                        </div>
                        <div class="text user-defined" v-if="isUserDefined(values.substrate)">
                            <input type="text" id="substrate-other" placeholder="Enter Your Substrate" v-model="values.udSubstrate">
                        </div>
                    </div>
                </fieldset>
                <fieldset v-if="showFastenerSection && options.fastener && options.fastener.length" class="flex fastener">
                    <div class="header">
                        <h3>Define the Fastener</h3>
                        <div class="help">
                            <i class="fa fa-question inline-popup" data-mfp-src="#fastener-help"></i>
                        </div>
                    </div>
                    <div>
                        <label for="anchor">
                            Fastener
                        </label>
                        <div v-if="options.fastener && options.fastener.length == 1" v-text="options.fastener[0]"></div>
                        <div v-else class="select">
                            <select id="anchor" placeholder="Select Your Anchor" v-model="values.fastener" @change="updateOptions('fastener')">
                                <option v-for="option in options.fastener" :value="option" v-text="option"></option>
                            </select>
                        </div>
                        <div class="text user-defined" v-if="isUserDefined(values.fastener)">
                            <input type="text" id="fastener-other" placeholder="Enter Fastener Name" v-model="values.udFastener">
                        </div>
                    </div>
                    <div v-if="options.fastenerOrientation && options.fastenerOrientation.length">
                        <label for="fastener-orientation">
                            Fastener Orientation
                        </label>
                        <div v-if="options.fastenerOrientation && options.fastenerOrientation.length == 1" v-text="options.fastenerOrientation[0]"></div>
                        <div v-else class="select">
                            <select id="fastener-orientation" placeholder="Select Your Fastener Orientation" v-model="values.fastenerOrientation" @change="updateOptions('fastenerOrientation')">
                                <option v-for="option in options.fastenerOrientation" :value="option" v-text="option"></option>
                            </select>
                        </div>
                    </div>
                    <div v-if="options.fastenerSize && options.fastenerSize.length">
                        <label for="fastener-size">
                            Fastener Size
                        </label>
                        <div v-if="options.fastenerSize && options.fastenerSize.length == 1" v-text="toFrac(options.fastenerSize[0])"></div>
                        <div v-else class="select">
                            <select id="fastener-size" placeholder="Select Your Fastener Size" v-model="values.fastenerSize" @change="updateOptions('fastenerSize')">
                                <option v-for="option in options.fastenerSize" :value="option" v-text="useMetric(1) ? inOrCm(option) : toFrac(option)"></option>
                            </select>
                        </div>
                        <span v-text="useMetric(1) ? 'cm.' : 'in.'"></span>
                    </div>
                    <div v-if="showFastenerEmbedment && options.fastenerEmbedment && options.fastenerEmbedment.length">
                        <label for="fastener-embedment">
                            Fastener Embedment
                        </label>
                        <div class="text embedment-other" v-if="isUserDefined(values.fastener)">
                            <input type="number" min="0" id="embedment-other" placeholder="0" v-model="values.udEmbedment" @change="updateOptions('udEmbedment')">
                        </div>
                        <div v-else>
                            <div v-if="options.fastenerEmbedment && options.fastenerEmbedment.length == 1" v-text="toFrac(options.fastenerEmbedment[0])"></div>
                            <div v-else class="select">
                                <select id="fastener-embedment" placeholder="Fastener Embedment" v-model="values.fastenerEmbedment" @change="updateOptions('fastenerEmbedment')">
                                    <option v-for="option in options.fastenerEmbedment" :value="option" v-text="useMetric(1) ? inOrCm(option) : toFrac(option)"></option>
                                </select>
                            </div>
                        </div>
                        <span v-text="useMetric(1) ? 'cm.' : 'in.'" class="measure"></span>
                        <p class="note" v-if="isUserDefined(values.fastener)">
                            Requires decimal input
                        </p>
                    </div>
                    <div v-if="isUserDefined(values.fastener)">
                        <label for="fastener-load">
                            Fastener Load
                        </label>
                        <div class="text embedment-other">
                            <input type="number" min="0" id="fastener-load" placeholder="0" v-model="values.udFastenerLoad" @change="updateOptions('udFastenerLoad')">
                        </div>
                        <span v-text="useMetric(1) ? 'kg.' : 'lbs.'" class="measure"></span>
                    </div>
                </fieldset>
                <fieldset v-if="showStructuralAttachmentSection" class="flex structure">
                    <div class="header">
                        <h3>Define the Structural Attachment</h3>
                        <div class="help">
                            <i class="fa fa-question inline-popup" data-mfp-src="#structural-help"></i>
                        </div>
                    </div>
                    <div v-if="options.structuralAttachment && options.structuralAttachment.length" class="structural-attachment">
                        <label for="structural-attachment">
                            Structural Attachment
                        </label>
                        <div v-if="options.structuralAttachment && options.structuralAttachment.length == 1" v-text="options.structuralAttachment[0]"></div>
                        <div v-else class="select">
                            <select id="strutual-attachment" placeholder="Structural Attachment" v-model="values.structuralAttachment" @change="updateOptions('structuralAttachment')">
                                <option v-for="option in options.structuralAttachment" :value="option" v-text="option"></option>
                            </select>
                        </div>
                    </div>
                    <div class="text thickness">
                        <label for="beam-thickness">
                            Beam Thickness
                        </label>
                        <input-number :cmToIn="useMetric(1)" :min="0" :step=".01" :max="1.5" v-model.number="values.beamThickness" @change="updateOptions('beamThickness')" />
                        <span class="measurement">
                            <span v-if="useMetric(1)">cm.</span>
                            <span v-else>in.</span>
                        </span>
                        <p class="note">
                            <span v-if="useMetric(1)">(Between .025cm and 3.81cm.)</span>
                            <span v-else>(Between 0.01" and 1.50")</span>
                        </p>
                    </div>
                    <div v-if="options.loadOrientation && options.loadOrientation.length">
                        <label for="load-orientation">
                            Load Orientation
                        </label>
                        <div v-if="options.loadOrientation && options.loadOrientation.length == 1" v-text="options.loadOrientation[0]"></div>
                        <div v-else class="select">
                            <select id="load-orientation" placeholder="Load Orientation" v-model="values.loadOrientation" @change="updateOptions('loadOrientation')">
                                <option v-for="option in options.loadOrientation" :value="option" v-text="option"></option>
                            </select>
                        </div>
                    </div>
                </fieldset>

                <fieldset class="flex">
                    <h3>Define the Brace Type</h3>
                    <div>
                        <label for="angle-min">
                            Brace Angle (θ)
                        </label>
                        <div v-if="angleMinimumOptions.length == 1" v-text="angleMinimumOptions[0]"></div>
                        <div v-else class="select">
                            <select id="angle-min" placeholder="Min" v-model="values.angleMin" @change="updateOptions('angleMin')">
                                <option v-for="angle in angleMinimumOptions" :value="angle" v-text="angle"></option>
                            </select>

                        </div>
                        <label for="angle-min" class="auto">
                            min
                        </label>
                        <div v-if="angleMaximumOptions.length == 1" v-text="angleMaximumOptions[0]"></div>
                        <div v-else class="select">
                            <select id="angle-max" placeholder="Max" v-model="values.angleMax" @change="updateOptions('angleMax')">
                                <option v-for="angle in angleMaximumOptions" :value="angle" v-text="angle"></option>
                            </select>
                        </div>
                        <label class="auto " for="angle-max">
                            max
                        </label>
                    </div>
                    <div>
                        <label for="lr">
                           l/r max
                        </label>
                        <div class="select">
                            <select id="lr" placeholder="L/R" v-model="values.lrMax" @change="updateOptions('lrMax')">
                                <option v-for="option in lrMaxOptions" v-text="option" :value="option"></option>
                            </select>
                        </div>
                        <p class="note">
                            Slenderness ratio
                        </p>
                    </div>
                    <div>
                        <label for="brace-arm-size">
                            Brace Pipe Size
                        </label>
                        <div class="select">
                            <select id="brace-arm-size" placeholder="Brace Pipe Size" v-model="values.armSize" @change="updateOptions('armSize')">
                                <option v-for="val in braceArmSizeOptions" :value="val" v-text="npsOrDn(toFrac(val))"></option>
                            </select>
                        </div>
                    </div>
                </fieldset>
                <fieldset class="flex" v-if="allValuesFilled && fastenerBraceInfo.braceArmCapacity">
                    <span class="note net-vertical-message" v-if="netVerticalReactionMessage" v-text="netVerticalReactionMessage"></span>

                    <div>
                        <label for="">Brace Pipe Capacity</label>
                        <div v-text="lbOrKg(fastenerBraceInfo.braceArmCapacity, true, true)"></div>
                    </div>
                    <div>
                        <label for="">Brace Pipe Length</label>
                        <div v-if="useMetric(1)" v-text="ftOrM(fastenerArmLengthDecimal, true)"></div>
                        <div v-else v-text="fastenerArmLength"></div>
                    </div>
                    <div v-if="values.fastenerOrientation">
                        <label for="">Fastener Orientation</label>
                        <div v-text="fastenerBraceInfo.fastenerOrientations"></div>
                    </div>
                    <div>
                        <label for="">Least Radius of Gyration</label>
                        <div v-text="inOrCm(fastenerBraceInfo.leastRadiusOfGyration, true)"></div>
                    </div>
                </fieldset>
            </section>
            <section v-if="structuralAttachments.length">
                <div class="structural-attachments">
                    <h3>Choose your Structural Attachments</h3>
                    <fieldset>
                        <div class="header">
                            <div>Option Number</div>
                            <div>Primary<br>(choose 1)</div>
                            <div>Secondary</div>
                            <div v-if="showFastenerSection">Fastener</div>
                            <div v-if="showFastenerSection">Fastener Load Capacity</div>
                            <div v-if="showStructuralAttachmentSection">
                                Structural Attachment,<br/>
                                Size,<br/>
                                Capacity
                            </div>
                            <div>Swivel Connection,<br/>
                                Size,<br/>
                                Capacity
                            </div>
                        </div>
                        <div v-for="(attachment, index) in structuralAttachments">
                            <div v-text="`${index + 1}`"></div>

                            <div>
                                <input type="checkbox" :name="`primary-secondary-${index}`" v-model="attachment.primary" @change="choosePrimaryStructural(attachment)" :id="`attachments-primary-${index}`">
                                <label :for="`attachments-primary-${index}`"></label>
                            </div>

                            <div>
                                <input type="checkbox" :name="`primary-secondary-${index}`" v-model="attachment.secondary" @change="chooseSecondaryStructural(attachment)" :id="`attachments-secondary-${index}`" />
                                <label :for="`attachments-secondary-${index}`"></label>
                            </div>

                            <div v-if="showFastenerSection" v-text="attachment.fastener"></div>
                            <div v-if="showFastenerSection" v-text="lbOrKg(attachment.fastenerLoadCapacity, true, true)"></div>
                            <div v-if="showStructuralAttachmentSection">
                                <modal :index="index" :result="attachment.structuralAttachmentSubmittal" :text="attachment.structuralAttachment"/>,
                                {{ lbOrKg(attachment.structuralAttachmentCapacity, true, true) }}, <br>
                                {{ attachment.structuralAttachmentSize }}
                            </div>
                            <div>
                                <modal :index="index" :result="attachment.swivelSubmittal" :text="attachment.swivelConnection"/>,
                                {{ attachment.swivelSize }}, <br>
                                {{ lbOrKg(attachment.swivelConnectionCapacity, true, true) }}
                            </div>
                        </div>
                    </fieldset>
                </div>
            </section>
            <p class="error" v-else-if="allValuesFilled">
                Unfortunately no structural attachments match your specifications.
            </p>

            <section v-if="pipeAttachments.length">
                <div class="pipe-attachments">
                    <h3>Choose your Pipe Attachments</h3>
                    <fieldset>
                        <div class="header">
                            <div>Option Number</div>
                            <div>Primary<br>(choose 1)</div>
                            <div>Secondary</div>
                            <div>Pipe Attachment</div>
                            <div>Pipe Attachment Size</div>
                            <div>Pipe Attachment Capacity</div>
                        </div>
                        <div v-for="(pipe, index) in pipeAttachments">
                            <div v-text="`${index + 1}`"></div>

                            <div>
                                <input type="checkbox" v-model="pipe.primary" @change="choosePrimaryPipe(pipe)" :id="`pipes-primary-${index}`">
                                <label :for="`pipes-primary-${index}`"></label>
                            </div>

                            <div>
                                <input type="checkbox" v-model="pipe.secondary" @change="chooseSecondaryPipe(pipe)" :id="`pipes-secondary-${index}`" />
                                <label :for="`pipes-secondary-${index}`"></label>
                            </div>

                            <div>
                                <modal :index="index" :result="pipe.pipeAttachmentSubmittal" :text="pipe.pipeAttachment" />
                            </div>
                            <div v-text="pipe.pipeAttachmentSize"></div>
                            <div v-text="lbOrKg(pipe.pipeAttachmentCapacity, true, true)"></div>
                        </div>
                    </fieldset>
                </div>
            </section>
            <p class="error" v-else-if="allValuesFilled">
                Unfortunately no pipe attachments match your specifications.
            </p>

            <section>
                <seismic-actions
                    @save="reviewThenSave"
                    @next="saveThenNext"
                    :fadeSave="!hasChanges"
                    :save-button-finished-text="saveButtonFinishedText"
                    :saving="saving"></seismic-actions>
            </section>
        </main>
        <aside class="primary">
            <seismic-sidebar :design="this.seismicDesignRecord" :brace="this"></seismic-sidebar>
        </aside>
        <div id="define-structure-help" class="inline-modal mfp-hide">
            <div>
                <p>
                    Anvil’s seismic software provides solutions for fire protection piping systems installed in the following structures with the fasteners listed in accordance with NFPA 13-2016.
                </p>
                <table>
                    <tr>
                        <th>STRUCTURE</th>
                        <th>SUBSTRATE</th>
                        <th>FASTENERS</th>
                    </tr>
                    <tr>
                        <td>
                            <strong>Cracked Concrete</strong>
                        </td>
                        <td>
                            Lightweight Cracked Concrete - 3000 psi <br>
                            Lightweight Cracked Concrete - 3000 psi - Metal Deck <br>
                            Normal Weight Cracked Concrete - 3000 psi <br>
                            Normal Weight Cracked Concrete - 4000 psi <br>
                            Normal Weight Cracked Concrete - 6000 psi
                        </td>
                        <td>
                            Wedge Anchor
                            Undercut Anchor
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>Wood</strong>
                        </td>
                        <td>
                            Wood - SG ≤ 0.35 <br>
                            Wood - SG = 0.36 to 0.49 <br>
                            Wood - SG = 0.50 to 0.65 <br>
                            Wood - SG = 0.66 to 0.73
                        </td>
                        <td>
                            Lag Screw
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>Lumber / Timbers</strong>
                        </td>
                        <td>
                            Saw Lumber or Glue-Laminated Timbers - SG ≤ 0.35 <br>
                            Saw Lumber or Glue-Laminated Timbers - SG = 0.36 to 0.49 <br>
                            Saw Lumber or Glue-Laminated Timbers - SG = 0.50 to 0.65 <br>
                            Saw Lumber or Glue-Laminated Timbers - SG = 0.66 to 0.73
                        </td>
                        <td>
                            Thru-Bolt
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>Steel</strong>
                        </td>
                        <td>
                            Steel w/ Thru Hole
                        </td>
                        <td>
                            Steel Bolt
                        </td>
                    </tr>
                    <tr>
                        <td>
                            <strong>I-Beam or Joist</strong>
                        </td>
                        <td>
                            Horizontal Beam Flange <br>
                            Vertical Joist Flange
                        </td>
                        <td>
                            Anvil Brand Structural Attachment
                        </td>
                    </tr>
                </table>
                <p>
                    If a structure, substrate, fastener, fastener embedment, fastener orientation, structural attachment, pipe attachment, or swivel attachment is not listed within the software it is for one of the following reasons:
                </p>
                <ol>
                    <li>
                       The seismic design load exceeds all horizontal allowable loads for connections to the structure or substrate.
                    </li>
                    <li>
                        For the chosen fastener, the seismic design load exceeds all horizontal allowable loads for a specific fastener orientation.
                    </li>
                    <li>
                        The seismic design load exceeds all horizontal allowable loads for the structural attachment, swivel attachment, or pipe attachment at the chosen installation angles.
                    </li>
                    <li>
                        No available structural attachment, swivel attachment, or pipe attachment may connect to the chosen brace size, anchor size, beam thickness, etc.
                    </li>
                </ol>
                <p>
                    If a substrate, fastener, fastener embedment, or fastener orientation is chosen for the user, it is because only one option is available with the chosen seismic design load, structure, or fastener.
                </p>
            </div>
        </div>
        <div id="fastener-help" class="inline-modal mfp-hide">
            <div>
                <img src="/img/seismic/ABC-fastener.jpg" alt="ABC fastener example">
            </div>
            <div>
                <img src="/img/seismic/DEF-fastener.jpg" alt="ABC fastener example">
            </div>
            <div>
                <img src="/img/seismic/GHI-fastener.jpg" alt="ABC fastener example">
            </div>
        </div>
        <div id="structural-help" class="inline-modal mfp-hide">
            <div>
                <img src="/img/seismic/parallel-structure.jpg" alt="Parallel structure example">
            </div>
            <div>
                <img src="/img/seismic/perpendicular-structure.jpg" alt="Perpendicular structure example">
            </div>
        </div>
    </seismic-layout>
</template>

<script>
import ClockvineMixin from 'vue/common/clockvine-mixin';
import SeismicDesignLoader from './mixins/seismic-design-loader';
import InputNumber from 'vue/common/_inputNumber';

import SeismicLayout from './layout';
import SeismicHeader from './header';
import SeismicSidebar from './shared/sidebar';
import SeismicErrors from './shared/errors';
import SeismicActions from './shared/actions';
import Modal from './_modal';
import MagnificPopup from 'magnific-popup';
import Fraction from 'fraction.js';

import { mapGetters, mapState } from 'vuex';
import { mapKeyedGetters } from '../../common/vuex-extra';

export default {
    components: {
        SeismicLayout,
        SeismicHeader,
        SeismicSidebar,
        SeismicActions,
        SeismicErrors,

        InputNumber,
        Modal,
    },

    mixins: [
        ClockvineMixin,
        SeismicDesignLoader,
    ],

    data() {
        return {
            type: 'seismicBraceConfiguration',

            saveButtonFinishedText: 'SAVED!',

            values: {
                structure: null,
                udStructure: null,
                substrate: null,
                udSubstrate: null,
                fastener: null,
                udFastener: null,
                fastenerOrientation: null,
                fastenerSize: null,
                fastenerEmbedment: null,
                udEmbedment: null,
                udFastenerLoad: null,
                structuralAttachment: null,
                loadOrientation: null,
                beamThickness: null,

                angleMin: null,
                angleMax: null,
                lrMax: null,
                armSize: null,

                bracePipeCapacity: null,
                bracePipeLength: null,
            },

            structuralAttachments: [],
            pipeAttachments: [],
        };
    },

    watch: {
        options: {
            handler() {
                let shouldRecurse = false;

                this.fields.forEach(f => {
                    if (this.options[f] && !this.options[f].includes(this.values[f])) {
                        this.values[f] = null;
                    }

                    // If there's option one option, set it to that.
                    if (this.options[f] && this.options[f].length == 1) {

                        // If this option wasn't previously set, make sure we re-updateOptions
                        if (this.values[f] == null && this.options[f][0] != null) {
                            shouldRecurse = true;
                        }

                        this.values[f] = this.options[f][0];
                    }
                });

                // If at least one value is set now that wasn't before, re-updateOptions
                if (shouldRecurse) {
                    this.updateOptions();
                }

                if (this.angleMinimumOptions.length == 1) {
                    this.values.angleMin = this.angleMinimumOptions[0];
                }

                if (this.angleMaximumOptions.length == 1) {
                    this.values.angleMax = this.angleMaximumOptions[0];
                }
            },
            deep: true
        },

        "values.angleMin"() {
            this.$store.dispatch('seismicAttachment/fetchNetVerticalReactionMessage', {
                angleMin: this.values.angleMin,
            });
        },

        record() {
            this.init();
        },

        structuralAttachmentOptions: {
            handler() {
                // This bit of madness is to preserve which options are checked
                // when coming to this page via navigation.
                for (let i = 0; i < this.structuralAttachments.length; i++) {
                    if (i < this.structuralAttachmentOptions.length
                        && this.structuralAttachmentsAreEqual(this.structuralAttachments[i], this.structuralAttachmentOptions[i])) {
                        this.structuralAttachmentOptions[i].primary = this.structuralAttachments[i].primary;
                        this.structuralAttachmentOptions[i].secondary = this.structuralAttachments[i].secondary;
                    }
                }

                this.structuralAttachments = this.structuralAttachmentOptions;
                this.record.structuralAttachmentOptions = this.structuralAttachmentOptions;
            },
            deep: true,
        },

        pipeAttachmentOptions: {
            handler() {
                // This bit of madness is to preserve which options are checked
                // when coming to this page via navigation.
                for (let i = 0; i < this.pipeAttachments.length; i++) {
                    if (i < this.pipeAttachmentOptions.length
                        && this.pipeAttachmentsAreEqual(this.pipeAttachments[i], this.pipeAttachmentOptions[i])) {
                        this.pipeAttachmentOptions[i].primary = this.pipeAttachments[i].primary;
                        this.pipeAttachmentOptions[i].secondary = this.pipeAttachments[i].secondary;
                    }
                }

                this.pipeAttachments = this.pipeAttachmentOptions;
                this.record.pipeAttachmentOptions = this.pipeAttachmentOptions;
            },
            deep: true,
        },
    },

    computed: {
        cp() {
            // this method is for the sidebar component
            return this.record.cp;
        },

        name() {
            return this.record.name;
        },

        ...mapState({
            "fields": state => state.seismicAttachment.fields,
            "options": state => state.seismicAttachment.options,
            "fastenerBraceInfo": state => state.seismicAttachment.fastenerBraceInfo,
            "structuralAttachmentOptions": state => state.seismicAttachment.structuralAttachmentOptions,
            "pipeAttachmentOptions": state => state.seismicAttachment.pipeAttachmentOptions,
            "netVerticalReactionMessage": state => state.seismicAttachment.netVerticalReactionMessage,
            "braceArmSizeOptions": state => state.seismicAttachment.braceArmSizeOptions,
        }),

        fastenerArmLength() {
            return `${this.fastenerBraceInfo.braceArmLength.lengthFt}'${this.fastenerBraceInfo.braceArmLength.lengthIn}"`;
        },

        fastenerArmLengthDecimal() {
            return this.fastenerBraceInfo.braceArmLength.lengthFt + this.fastenerBraceInfo.braceArmLength.lengthIn / 12;
        },

        showFastenerSection() {
            return (['Cracked Concrete', 'Wood (Lag Bolt)', 'Lumber/Timbers (Thru Bolt)', 'Steel'].includes(this.values.structure) || this.isUserDefined(this.values.structure));
        },

        showFastenerEmbedment() {
            return (['Cracked Concrete', 'Wood (Lag Bolt)', 'Lumber/Timbers (Thru Bolt)', 'Steel'].includes(this.values.structure) || this.isUserDefined(this.values.structure));
        },

        showStructuralAttachmentSection() {
            return ['I-Beam/Joist'].includes(this.values.structure);
        },

        lrMaxOptions() {
            if (this.seismicDesignRecord) {
                return [100, 200, 300].filter(i => i <= this.seismicDesignRecord.max);
            } else {
                return [100, 200, 300];
            }
        },

        angleMinimumOptions() {
            if (this.record.orientation == 'riser-wall') {
                return [30, 45];
            } else {
                return [30, 45, 60];
            }
        },

        angleMaximumOptions() {
            let options = [];
            if (this.record.orientation == 'riser-wall') {
                options = [44, 59];
            } else {
                options = [44, 59, 90];
            }

            return options.filter(a => a > this.values.angleMin);
        },

        allValuesFilled() {
            let state = this.values.structure
                && this.values.substrate
                && this.values.angleMin
                && this.values.angleMax
                && this.values.lrMax
                && this.values.armSize;

            if (this.showFastenerSection) {
                state = state
                    && this.values.fastener
                    && this.values.fastenerOrientation
                    && this.values.fastenerSize;

                if (this.values.structure != 'Steel') {
                    state = state && this.values.fastenerEmbedment;
                }
                if (this.isUserDefined(this.values.structure)) {
                    state = state && this.values.udFastenerLoad;
                }
            }

            if (this.showStructuralAttachmentSection) {
                state = state
                    && this.values.structuralAttachment
                    && this.values.loadOrientation
                    && this.values.beamThickness;
            }

            return state;
        },
    },

    methods: {
        init() {
            Object.entries(this.values).forEach(([k, v]) => {
                this.values[k] = this.record[k];
            });

            this.$store.dispatch('seismicAttachment/setValues', this.values).catch(() => {});

            let structuralAttachmentOptions = this.record.structuralAttachmentOptions;
            let pipeAttachmentOptions = this.record.pipeAttachmentOptions;

            if (structuralAttachmentOptions && structuralAttachmentOptions.length) {
                this.$store.commit('seismicAttachment/setStructuralAttachmentOptions', structuralAttachmentOptions);
            } else {
                this.$store.dispatch('seismicAttachment/fetchStructuralAttachmentOptions').catch(() => {});
            }

            if (pipeAttachmentOptions && pipeAttachmentOptions.length) {
                this.$store.commit('seismicAttachment/setPipeAttachmentOptions', pipeAttachmentOptions);
            } else {
                this.$store.dispatch('seismicAttachment/fetchPipeAttachmentOptions').catch(() => {});
            }

            this.$store.dispatch('seismicAttachment/fetchFastenerBraceInfo').catch(() => {});
            this.$store.dispatch('seismicAttachment/fetchBraceArmSizeOptions').catch(() => {});
        },

        updateOptions(columnChanged) {
            let fields = Object.keys(this.values);

            for (let i = 0; i < fields.length; i++) {
                let field = fields[i];
                if (this.options[field] && !this.options[field].includes(this.values[field])) {
                    this.values[field] = null;
                }
            }

            // This is only because beamThickness is an input and not a dropdown
            if (this.values.structure != 'I-Beam/Joist') {
                this.values.beamThickness = null;
            }

            this.$store.dispatch('seismicAttachment/setValues', this.values).then(() => {
                this.$nextTick(() => {
                    var $popup = $('.inline-popup');
                    if ($popup.length) {
                        $popup.magnificPopup({
                            type:'inline'
                        });
                    }
                });
            })
                .catch(() => {})
                .then(() => {
                    this.$store.dispatch('seismicAttachment/fetchStructuralAttachmentOptions').catch(() => {});
                    this.$store.dispatch('seismicAttachment/fetchPipeAttachmentOptions').catch(() => {});
                    this.$store.dispatch('seismicAttachment/fetchFastenerBraceInfo').catch(() => {});
                    this.$store.dispatch('seismicAttachment/fetchBraceArmSizeOptions').catch(() => {});
                });
        },

        updateStructuralAttachments() {
            this.$store.commit('seismicAttachment/setStructuralAttachmentOptions', this.structuralAttachments);
        },

        updatePipeAttachments() {
            this.$store.commit('seismicAttachment/setPipeAttachmentOptions', this.pipeAttachments);
        },

        validateThenSave() {
            this.saving = true;
            let valid = false;

            if (
                this.structuralAttachments.findIndex(i => i.primary) !== -1 &&
                this.structuralAttachments.findIndex(i => i.secondary) !== -1 &&
                this.pipeAttachments.findIndex(i => i.primary) !== -1 &&
                this.pipeAttachments.findIndex(i => i.secondary) !== -1
            ) {
                valid = true
            }
        },

        reviewThenSave() {
            this.record.needsReview = false;
            return this.save();
        },

        saveThenNext() {
            this.reviewThenSave().then(() => {
                this.$router.push({name: 'seismic-brace-summary', params: {
                    id: this.seismicDesignId
                }});
            })
        },

        getRecord() {
            return {...this.record, ...this.values};
        },

        getUrlParams() {
            return { seismicDesignId: this.$route.params.seismicDesignId };
        },

        getRecordParams() {
            let record = Object.assign({},
                this.record,
                this.values,
                {
                    bracePipeCapacity: this.fastenerBraceInfo.braceArmCapacity,
                    bracePipeLength: this.fastenerBraceInfo.braceArmLength,
                    leastRadiusOfGyration: this.fastenerBraceInfo.leastRadiusOfGyration,
                    calculatedFastenerOrientation: this.fastenerBraceInfo.fastenerOrientations
                }
            );

            return Object.assign({}, this.getUrlParams(), { data: record });
        },

        choosePrimaryStructural(attachment) {
            if (attachment.primary) {
                this.structuralAttachments.forEach(attachment => attachment.primary = false);
                attachment.primary = true;
                attachment.secondary = false;
            }

            this.updateStructuralAttachments();
        },

        chooseSecondaryStructural(attachment) {
            if (attachment.secondary) {
                attachment.primary = false;
            }

            this.updateStructuralAttachments();
        },

        choosePrimaryPipe(attachment) {
            if (attachment.primary) {
                this.pipeAttachments.forEach(attachment => attachment.primary = false);
                attachment.primary = true;
                attachment.secondary = false;
            }

            this.updatePipeAttachments();
        },

        chooseSecondaryPipe(attachment) {
            if (attachment.secondary) {
                attachment.primary = false;
            }

            this.updatePipeAttachments();
        },

        toFrac: n => (new Fraction(n)).toFraction(true),

        structuralAttachmentsAreEqual(one, two) {
            let attrs = [
                'fastener', 'fastenerLoadCapacity', 'structuralAttachment', 'structuralAttachmentCapacity', 'structuralAttachmentSize', 'swivelConnection', 'swivelConnectionCapacity', 'swivelOrientiation', 'swivelSize'
            ];
            for (let attr of attrs) {
                if (one[attr] != two[attr]) {
                    return false;
                }
            }

            return true;
        },

        pipeAttachmentsAreEqual(one, two) {
            let attrs = ['pipeAttachment', 'pipeAttachmentCapacity', 'pipeAttachmentSize'];
            for (let attr of attrs) {
                if (one[attr] != two[attr]) {
                    return false;
                }
            }

            return true;
        },
    },

    mounted() {
        this.updateOptions();

        this.$store.dispatch('seismicPipes/fetchPipes').then(() => {
            this.$store.dispatch('seismicSystem/fetchAll', { id: this.id }).then(() => {
                this.init();
                this.$store.dispatch('seismicAttachment/fetchNetVerticalReactionMessage', {
                    angleMin: this.values.angleMin,
                });
                this.$store.dispatch('seismicAttachment/fetchOptions');
            });
        });

        var $popup = $('.inline-popup');
        if ($popup.length) {
            $popup.magnificPopup({
                type:'inline'
            });
        }
    },
}
</script>
